// © 2026 AttainIP Engineering
import { api } from './client';

export async function fetchDashboard(params?: {
  exclude_email_domain?: string;
  hide_internal?: boolean;
}) {
  const cleaned: Record<string, unknown> = {};
  if (params?.exclude_email_domain) {
    cleaned.exclude_email_domain = params.exclude_email_domain;
  } else if (params?.hide_internal) {
    cleaned.hide_internal = true;
  }
  const { data } = await api.get('/v1/dashboard', { params: cleaned });
  return data;
}

export async function fetchServices() {
  const { data } = await api.get('/v1/services');
  return data;
}

export async function fetchUsers(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  exclude_email_domain?: string;
  hide_internal?: boolean;
  sort_by?: string;
  order?: string;
}) {
  // Strip empty/undefined values so we don't send `?search=` literally
  // (which would match nothing because of the LIKE wildcard).
  // Falsy booleans are also dropped so we send hide_internal=true only
  // when the toggle is actually on.
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    cleaned[k] = v;
  }
  const { data } = await api.get('/v1/users', { params: cleaned });
  return data;
}

export async function fetchUser(userId: string) {
  const { data } = await api.get(`/v1/users/${userId}`);
  return data;
}

export async function fetchUserActivity(userId: string, params?: { limit?: number; offset?: number }) {
  const { data } = await api.get(`/v1/users/${userId}/activity`, { params });
  return data;
}

export async function fetchAnalyses(params?: {
  limit?: number;
  offset?: number;
  user_email?: string;
  status?: string;
  exclude_email_domain?: string;
  hide_internal?: boolean;
}) {
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    cleaned[k] = v;
  }
  const { data } = await api.get('/v1/analyses', { params: cleaned });
  return data;
}

export async function fetchAnalysis(analysisId: string) {
  const { data } = await api.get(`/v1/analyses/${analysisId}`);
  return data;
}

export async function fetchAnalysisChat(analysisId: string, userEmail?: string) {
  // Kicks off the Engine chat-history job and returns
  //   { status: "pending", job_id, user_email }  — poll via fetchJobStatus
  //   { status: "done", messages }               — synchronous Engine result (rare)
  const { data } = await api.get(`/v1/analyses/${analysisId}/chat`, {
    params: userEmail ? { user_email: userEmail } : undefined,
  });
  return data;
}

export async function fetchJobStatus(
  jobId: string,
  userEmail: string,
  jobType: 'PROCESS' | 'UPLOAD' = 'PROCESS',
) {
  // Poll the Engine workflow job via the admin BFF. Returns one of:
  //   { status: "running" | "pending", message?, progress? }
  //   { status: "done", messages }
  //   { status: "error", message }
  const { data } = await api.get(`/v1/jobs/${jobId}`, {
    params: { user_email: userEmail, type: jobType },
  });
  return data;
}

export async function fetchFeedback(params?: { resource_type?: string; limit?: number; user_id?: string }) {
  const { data } = await api.get('/v1/feedback', { params: { resource_type: 'analysis', ...params } });
  return data;
}

export async function fetchEvents(params?: { limit?: number; offset?: number; user_id?: string }) {
  const { data } = await api.get('/v1/events', { params });
  return data;
}

export async function fetchEvent(eventId: string) {
  const { data } = await api.get(`/v1/events/${eventId}`);
  return data;
}

export async function fetchPayments(params?: {
  limit?: number;
  offset?: number;
  transaction_type?: string;
  exclude_email_domain?: string;
  hide_internal?: boolean;
}) {
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    cleaned[k] = v;
  }
  const { data } = await api.get('/v1/payments', { params: cleaned });
  return data;
}

export async function fetchUserChats(userId: string) {
  const { data } = await api.get(`/v1/users/${userId}/chats`);
  return data;
}

export async function fetchUserChat(userId: string, sessionId: string) {
  const { data } = await api.get(`/v1/users/${userId}/chats/${sessionId}`);
  return data;
}

export async function fetchPublicChats() {
  const { data } = await api.get('/v1/public-chats');
  return data;
}

export async function fetchPublicChat(sessionId: string) {
  const { data } = await api.get(`/v1/public-chats/${sessionId}`);
  return data;
}

// --- Enterprise Admin (RFC-0211/0212 A2) ---

export async function fetchEnterprises() {
  const { data } = await api.get('/v1/enterprises');
  return data;
}

export async function fetchEnterprise(orgId: string) {
  const { data } = await api.get(`/v1/enterprises/${orgId}`);
  return data;
}

export async function fetchEnterpriseEntitlement(orgId: string) {
  const { data } = await api.get(`/v1/enterprises/${orgId}/entitlement`);
  return data;
}

export async function fetchEnterpriseUsers(orgId: string) {
  const { data } = await api.get(`/v1/enterprises/${orgId}/users`);
  return data;
}

export async function fetchEnterpriseMagicLink(orgId: string) {
  const { data } = await api.get(`/v1/enterprises/${orgId}/magic-link`);
  return data;
}

/** The firm's Workspace policy (RFC-0500 A5 / RFC-0206 W14).
 *  `workspace_enabled` is TRI-STATE: true / false / null-for-unset. */
export async function fetchEnterpriseWorkspace(orgId: string) {
  const { data } = await api.get(`/v1/enterprises/${orgId}/workspace`);
  return data as { org_id: string; workspace_enabled: boolean | null };
}

/** One workflow this environment's registry admits (RFC-0511 B). */
export interface AvailableWorkflow {
  wcfg: string;
  tier: string;
  friendly_type: string;
  about: string;
  price_amount: number;
  price_currency: string;
}

/** The firm's per-org enabled-workflow SET, plus the environment ceiling it
 *  sits under (RFC-0511 B).
 *
 *  ⚠️ `enabled_workflows` is TRI-STATE: `null` means the firm has NO per-org
 *  set and the environment decides — which is not the same as `[]`, an
 *  explicit "none of them". `available` is what this environment's registry
 *  admits; a firm's set may only narrow it. */
export interface EnterpriseWorkflows {
  org_id: string;
  enabled_workflows: string[] | null;
  available: AvailableWorkflow[];
  env?: string | null;
}

export async function fetchEnterpriseWorkflows(orgId: string) {
  const { data } = await api.get(`/v1/enterprises/${orgId}/workflows`);
  return data as EnterpriseWorkflows;
}

// --- Guest memberships, granted from the console (RFC-0510 W44) ---

/** One firm as `/v1/enterprises` returns it.
 *
 *  A configured-but-unactivated firm has `activated: false` and NO `org_id` —
 *  there is nothing to grant into, and nothing to be a home organisation
 *  either, so those rows are filtered out of every guest control. */
export interface EnterpriseSummary {
  domain: string;
  activated: boolean;
  org_id: string | null;
  ext_id: string | null;
  name: string;
  org_type?: string | null;
  user_count: number;
}

/** One guest membership of a firm, as Identity holds it.
 *
 *  `home_organization_id` is a UUID and is NEVER shown as one: the console
 *  resolves it to a name + domain from the enterprises list (RFC-0510 W30 —
 *  the lesson that cost us the last surface that asked for a raw UUID).
 *  `status` is `active` or `revoked`; a revoked row is retained history, not
 *  a deletion, which is why the list can show it at all. */
export interface GuestMembership {
  id: string;
  email: string;
  organization_id: string;
  kind: string;
  status: string;
  home_organization_id: string | null;
  granted_by_user_id: string;
  created_at: string | null;
  updated_at: string | null;
  revoked_at: string | null;
}

export async function fetchEnterpriseGuests(orgId: string, includeRevoked = false) {
  const { data } = await api.get(`/v1/enterprises/${orgId}/guests`, {
    params: { include_revoked: includeRevoked },
  });
  return data as { data: GuestMembership[]; total: number };
}

// --- Administrative case-ownership rescue (RFC-0510 W02) ---

export interface OwnedCase {
  case_id: number;
  name?: string;
  title?: string;
  status?: string;
  deleted?: boolean;
  [key: string]: unknown;
}

export interface OwnedCasesResponse {
  status: string;
  email: string;
  org_ext_id: string;
  /** The engine user the email resolved to, by an EXACT match on the identifier
   *  the engine derives for that address in that organisation. Surfaced so an
   *  operator can see WHO was resolved before acting on their cases. */
  engine_user_id: number;
  user_ext_id: string;
  candidates_searched: number;
  from: number | null;
  cases: OwnedCase[];
}

/** What `email` owns in ONE organisation (RFC-0510 C4, W38).
 *
 *  Takes the two things this console can actually show an operator: an email
 *  address (its Users surface searches them) and an organisation ext-id (its
 *  Enterprises listing returns one per firm). Both are REQUIRED — identity is
 *  per-organisation, so a lookup is always scoped to exactly one.
 *
 *  It used to take two engine-internal numbers that appear nowhere in the
 *  product; see the BFF route for how the email becomes an engine user id. */
export async function fetchOwnedCases(
  email: string,
  orgExtId: string,
  includeDeleted = false,
) {
  const { data } = await api.get('/v1/owned-cases', {
    params: { email, org_ext_id: orgExtId, include_deleted: includeDeleted },
  });
  return data as OwnedCasesResponse;
}
